import { useState, useEffect, useRef } from 'react'
import { useTenant } from '@/hooks/useTenant'
import { apiClient } from '@/lib/api'
import { Icons } from '@/lib/icons'
import type { BranchResponse } from '@/types/branches'

export function BranchSelector() {
  const { tenantId, activeBranchId, switchBranch, hasModule } = useTenant()
  const [branches, setBranches] = useState<BranchResponse[]>([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    // Only fetch if module is enabled and tenant is loaded
    if (!tenantId || !hasModule('branching')) return

    const fetchBranches = async () => {
      setLoading(true)
      try {
        const res = await apiClient.get<BranchResponse[]>('/api/v1/tenants/branches')
        setBranches(res)
        
        // If there's no active branch but we have branches, auto-select the main one
        if (!activeBranchId && res.length > 0) {
          const main = res.find(b => b.is_main_branch) || res[0]
          switchBranch(main.id)
        }
      } catch (err) {
        console.error('Failed to load branches for selector', err)
      } finally {
        setLoading(false)
      }
    }
    fetchBranches()
  }, [tenantId, hasModule, activeBranchId, switchBranch])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!hasModule('branching') || branches.length === 0) {
    return null
  }

  const activeBranch = branches.find(b => b.id === activeBranchId) || branches[0]

  return (
    <div ref={menuRef} style={{ position: 'relative' }}>
      <button 
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sz-space-2)',
          padding: 'var(--sz-space-2) var(--sz-space-3)',
          background: 'var(--sz-surface)',
          border: '1px solid var(--sz-border)',
          borderRadius: 'var(--sz-radius-lg)',
          cursor: 'pointer',
          color: 'var(--sz-text)',
          fontSize: '0.8125rem',
          fontWeight: 600,
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
          transition: 'border-color var(--sz-transition), background var(--sz-transition)',
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'var(--sz-surface-raised)'; e.currentTarget.style.borderColor = 'var(--sz-primary)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'var(--sz-surface)'; e.currentTarget.style.borderColor = 'var(--sz-border)'; }}
      >
        <Icons.Branches size={14} style={{ color: 'var(--sz-primary)' }} />
        <span>{activeBranch?.name || 'Select Branch'}</span>
        <Icons.ChevronDown size={14} style={{ opacity: 0.5 }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 'var(--sz-space-1)',
          background: 'var(--sz-surface)',
          border: '1px solid var(--sz-border)',
          borderRadius: 'var(--sz-radius-md)',
          boxShadow: 'var(--sz-shadow-md)',
          minWidth: 200,
          zIndex: 50
        }}>
          {loading ? (
            <div style={{ padding: 'var(--sz-space-3)', color: 'var(--sz-text-3)', fontSize: '0.875rem' }}>Loading...</div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 'var(--sz-space-1)' }}>
              {branches.map(branch => (
                <li key={branch.id}>
                  <button
                    onClick={() => {
                      switchBranch(branch.id)
                      setOpen(false)
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      padding: 'var(--sz-space-2) var(--sz-space-3)',
                      background: branch.id === activeBranchId ? 'var(--sz-surface-raised)' : 'transparent',
                      border: 'none',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderRadius: 'var(--sz-radius-sm)',
                      fontSize: '0.875rem',
                      color: branch.status === 'active' ? 'var(--sz-text)' : 'var(--sz-text-3)'
                    }}
                  >
                    <span>{branch.name}</span>
                    {branch.is_main_branch && <span style={{ fontSize: '0.7rem', background: 'var(--sz-primary)', color: 'white', padding: '2px 6px', borderRadius: 4 }}>Main</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
